/**
 * The in-space M2 API (plan §2.7): every type the space runtimes, the HUD, the views, the audio router, the
 * career runtime and the save system share, plus the small factories that build the shared structs, readouts,
 * pools and empty deltas. Wave-3 tasks meet only through this file (plan §5), so every view a presenter reads
 * (readouts, pools, the secured log, the group table) is fully specified here.
 *
 * Rules for everything below (plan §2.11): positions of rocks, chunks, wrecks, parts, stations and bays are
 * float64 world metres (`Vec3` objects and Float64Array columns) and reach three.js or Rapier only through
 * `toRenderSpace` and the physics origin (D-011, D-013). Readouts and pools are preallocated and mutated in place
 * every tick (D-014): readers never keep references to per-tick values and never allocate while reading.
 * Time is game time (`CareerMirror.gameTime`), never `sim.time`, so persisted timers survive a reload.
 *
 * Deviation from the plan's listed constructor signatures (M2-01, documented): every runtime is built from one
 * `SpaceContext` (the sim, data, seeded RNG, event ring, player input and tool state, mirror and models) plus the
 * runtimes it calls, because docking reads the player input, rocks and salvage update the player's nav target,
 * tools call the pickers of four runtimes and encounters read the stations and the docking phase.
 * Creation order (M2-04, `createSpaceWorld`): stations → docking → rocks → salvage → encounters → tools.
 *
 * FROZEN after wave 1 (plan §1.2): later tasks may add optional members only.
 */
import type { LoadedModel } from '../assets/loader'
import type { Career, CargoContents, MakerId } from '../career/types'
import type { Quat, Vec3 } from '../core/math'
import { createRng, type Rng } from '../core/rng'
import type { GameData } from '../data'
import type { RegionId } from '../galaxy/types'
import type { BaySocket } from '../docking/bay'
import type { DerelictSite } from '../salvage/generate'
import type { BoomMarks } from './booms'
import { holdCapacityT, holdMultInto } from '../career/hullFit'
import { moduleStats, resolveModule } from '../data/m2'
import {
  MAX_ANCHORS_PER_PART,
  MAX_PARTS_PER_WRECK,
  WARE_COUNT,
  type ComponentType,
  type M2Data,
  type ModuleId,
  type ModuleMk,
  type SlotId,
  type StationDef,
  type StationId,
  type WareId,
} from '../data/m2Schemas'
import type { Sim } from '../sim'
import type { UiStoreApi } from '../ui/contracts'
import { createSpaceEventRing, type SpaceEventRing } from './events'

export type Vec3Tuple = [number, number, number]
export type QuatTuple = [number, number, number, number]

// ---------------------------------------------------------------------------------------------
// Stages
// ---------------------------------------------------------------------------------------------

/** The five M2 sim stages in tick order (plan §2.3; M2-04's `SimStage` in src/sim.ts is this union). */
export const SPACE_STAGES = ['control', 'tools', 'physics', 'deaths', 'end'] as const
export type SpaceStage = (typeof SPACE_STAGES)[number]

/**
 * A stage method: runs once per tick in its stage, reads `sim.time`, `SIM_DT` and the mirror's game time,
 * allocation-free. Runtimes expose them as closures bound at creation (never rely on `this`), so M2-04 registers
 * them directly: `registerSimStage(sim, 'tools', space.rocks.tools)`.
 */
export type SpaceSubsystem = (sim: Sim) => void

// ---------------------------------------------------------------------------------------------
// Tool modes, nav targets and the player's tool input and state
// ---------------------------------------------------------------------------------------------

export const TOOL = { combat: 0, mining: 1, salvage: 2 } as const
export type ToolMode = (typeof TOOL)[keyof typeof TOOL]
export const TOOL_COUNT = 3

/**
 * Nav target kinds. Indices are runtime indices of that kind: `rock` = index into `sim.field` (the active
 * layout; a preset change clears every rock target, a depleted rock clears the targets that point at it),
 * `fragment` = fragment pool slot, `wreck` = wreck pool slot, `part` = part pool slot, `station` = station index.
 */
export const NAV = { none: 0, rock: 1, fragment: 2, wreck: 3, part: 4, station: 5 } as const
export type NavKind = (typeof NAV)[keyof typeof NAV]

export interface NavTarget {
  kind: NavKind
  /** -1 when `kind` is `none`. */
  index: number
}

export function createNavTarget(): NavTarget {
  return { kind: NAV.none, index: -1 }
}

export function setNav(target: NavTarget, kind: NavKind, index: number): void {
  target.kind = kind
  target.index = kind === NAV.none ? -1 : index
}

export function clearNav(target: NavTarget): void {
  target.kind = NAV.none
  target.index = -1
}

export function sameNav(a: Readonly<NavTarget>, b: Readonly<NavTarget>): boolean {
  return a.kind === b.kind && a.index === b.index
}

/**
 * The player's tool input for one frame, written by the career feature's `beforeTicks` (M2-22). The first tick of
 * the frame (the tools runtime's `control` stage) consumes the edge fields and clears them with
 * `clearToolInputEdges`, so later ticks of the same frame see none; `scanHeld` and `flightActive` are levels.
 */
export interface PlayerToolInput {
  /** -1 = none, else the `TOOL` mode asked for directly (M = Mining, B = Salvage). */
  modeRequest: number
  /** Backquote / pad R3 tap (W2 D2): Combat → Mining → Salvage → Combat. */
  cycleMode: boolean
  /**
   * A level, not an edge: true while the jettison offer is open (`features/jettison`), whose pad verb is R3 too, so
   * the career flow drops `toolCycle` instead of also cycling the tool (W2 gate).
   */
  cycleSuppressed: boolean
  /** The M1 `target` key in a tool mode: pick the nav target. */
  targetPressed: boolean
  /** R held / pad X held (Mining and Salvage modes). */
  scanHeld: boolean
  /** F / pad D-pad ↓ held 0.5 s: request clearance, or start auto-dock when cleared and close. */
  dockPressed: boolean
  /** Signed laser-power steps (mouse wheel, pad D-pad ← / →), each `mining.json fracture.powerStep`. */
  powerSteps: number
  /** Any flight axis beyond 0.1 this frame (cancels auto-dock). */
  flightActive: boolean
}

export function createPlayerToolInput(): PlayerToolInput {
  return { modeRequest: -1, cycleMode: false, cycleSuppressed: false, targetPressed: false, scanHeld: false, dockPressed: false, powerSteps: 0, flightActive: false }
}

/** Clears the edge fields once they are consumed. */
export function clearToolInputEdges(input: PlayerToolInput): void {
  input.modeRequest = -1
  input.cycleMode = false
  input.targetPressed = false
  input.dockPressed = false
  input.powerSteps = 0
}

/** The player's tool state (owned by the tools runtime; rocks and salvage update `nav` when a target dies). */
export interface PlayerToolState {
  mode: ToolMode
  /** Laser power the player set, 0..`mirror.tools.laserMaxPower`. */
  powerTarget: number
  /** Smoothed laser power (`fracture.powerSmoothing`), what the beam uses. */
  power: number
  /** Current nav target (rock or fragment in Mining, wreck in Salvage, station for docking). */
  nav: NavTarget
  /** 0..1 of `scan.holdSeconds` on the current nav target. */
  scanProgress: number
  /** Target of the last completed scan (`NAV.none` after a target change). */
  scanned: NavTarget
}

export function createPlayerToolState(): PlayerToolState {
  return { mode: TOOL.combat, powerTarget: 0, power: 0, nav: createNavTarget(), scanProgress: 0, scanned: createNavTarget() }
}

// ---------------------------------------------------------------------------------------------
// Career mirror (what the in-space systems read from the Career)
// ---------------------------------------------------------------------------------------------

export const COLLECTIONS_CODE = { none: 0, notice: 1, repo: 2 } as const
export type CollectionsCode = (typeof COLLECTIONS_CODE)[keyof typeof COLLECTIONS_CODE]

/** Tool stats derived from the fitted modules (M2-05 `deriveToolStats` produces the same values). */
export interface ToolStats {
  /** Mining laser max power, 0..1. */
  laserMaxPower: number
  heatCapacityMult: number
  chipRateMult: number
  /** Highest fracture tier the laser cracks. */
  maxTier: number
  cutMult: number
  spillPerSec: number
  tractorRange: number
  scannerRange: number
  scannerMk: ModuleMk
}

/**
 * Career values the in-space systems read every tick, written by the career runtime after every command.
 * Collectors write `cargoUsedT` at once when they accept ore or a part, so a second chunk in the same tick sees
 * the fuller hold before the career command runs.
 */
export interface CareerMirror {
  /**
   * THE game clock (seconds of in-space sim time). `registerSpaceStages` (M2-04) appends a clock step as the last
   * function of the `end` stage (`mirror.gameTime += SIM_DT`), so during a tick it is that tick's start time and
   * it never runs while the sim is held (docked). The career runtime sets it on new, continue and load, and folds
   * it into the Career (`withGameTime`) when a command runs or a save is written.
   */
  gameTime: number
  /** The hold (tonnes): the hull-rated hold (M10 P5a4a, `career/hullFit.ts holdCapacityT`; the racks' own when the hull has no `fit`). */
  cargoCapacityT: number
  cargoUsedT: number
  /**
   * M10 P5a6a: the hold divisor of each M2 ware (`WARE_IDS` index) on the flown hull (`career/hullFit.ts holdMultInto`;
   * a collected chunk adds `tonnes ÷ holdMult[ware]` to `cargoUsedT`). Absent or 1 = today's 1×.
   */
  holdMult?: Float64Array
  /** Heaviest part the cargo racks take (tonnes). */
  maxPartMassT: number
  /** Base-price estimate of the hold (credits), for the encounter director. */
  cargoValue: number
  collections: CollectionsCode
  /** Collections paused after a defeated repo crew (no repo spawns). */
  repoPaused: boolean
  owned: boolean
  tools: ToolStats
  /** Index of the player's ship def in `sim.defs`. */
  playerDefIndex: number
}

/** Tool stats of a fit (writes into `out` when given). */
export function toolStatsForFit(data: Pick<M2Data, 'modules'>, fitted: Readonly<Record<SlotId, ModuleId>>, out?: ToolStats): ToolStats {
  const mining = moduleStats(data, 'mining', fitted.miningLaser)
  const salvage = moduleStats(data, 'salvage', fitted.salvageBeam)
  const scanner = resolveModule(data, fitted.scanner)
  const scannerStats = moduleStats(data, 'scanner', fitted.scanner)
  const t = out ?? {
    laserMaxPower: 0,
    heatCapacityMult: 1,
    chipRateMult: 1,
    maxTier: 0,
    cutMult: 1,
    spillPerSec: 0,
    tractorRange: 0,
    scannerRange: 0,
    scannerMk: 1,
  }
  t.laserMaxPower = mining.maxPower
  t.heatCapacityMult = mining.heatCapacityMult
  t.chipRateMult = mining.chipRateMult
  t.maxTier = mining.maxTier
  t.cutMult = salvage.cutMult
  t.spillPerSec = salvage.spillPerSec
  t.tractorRange = salvage.tractorRange
  t.scannerRange = scannerStats.range
  t.scannerMk = scanner.mk
  return t
}

/** A mirror for the stock start fit with an empty hold (the career runtime overwrites it on new or load). */
export function createCareerMirror(data: Pick<M2Data, 'modules' | 'career'>): CareerMirror {
  const start = data.career.start
  const cargo = moduleStats(data, 'cargo', start.fitted.cargo)
  return {
    gameTime: 0,
    cargoCapacityT: holdCapacityT(start.ship, start.fitted, data),
    cargoUsedT: 0,
    holdMult: holdMultInto(start.ship, new Float64Array(WARE_COUNT)),
    maxPartMassT: cargo.maxPartMassT,
    cargoValue: 0,
    collections: COLLECTIONS_CODE.none,
    repoPaused: false,
    owned: false,
    tools: toolStatsForFit(data, start.fitted),
    playerDefIndex: start.shipDefIndex,
  }
}

// ---------------------------------------------------------------------------------------------
// Readouts (plain objects mutated in place; the HUD, audio and fx read them)
// ---------------------------------------------------------------------------------------------

export const MINING_MODE = { chip: 0, fracture: 1 } as const
export type MiningMode = (typeof MINING_MODE)[keyof typeof MINING_MODE]

export interface FractureReadout {
  /** A fracture is engaged on `MiningReadout.rock`. */
  active: boolean
  /** 0..1; 1 = success. */
  progress: number
  /** 0..1; 1 = burst. */
  instability: number
  /** Stability window centre and width on the 0..1 power bar. */
  windowCenter: number
  windowWidth: number
  /** Resistance tier of the rock (0-based). */
  tier: number
  /** The fitted laser cannot crack this tier ("RESISTANCE TOO HIGH: Mk II laser"). */
  tooHard: boolean
}

export interface MiningReadout {
  /** The player is firing the mining beam (Mining mode, trigger held, not overheated). */
  beamOn: boolean
  /** The beam touches `rock`. */
  contact: boolean
  /** Rock or fragment under the beam (`NAV.none` without contact). */
  rock: NavTarget
  /** CHIP or FRACTURE (`MINING_MODE`) of `rock`. */
  mode: MiningMode
  /** Smoothed laser power, 0..1. */
  power: number
  /** The fitted laser's max power (the HUD's Mk line). */
  maxPower: number
  /** 0..1; 1 = overheated. */
  heat: number
  overheated: boolean
  /** Seconds of forced cooldown left. */
  cooldownLeft: number
  /** Seconds of contact toward the next chunk (CHIP). */
  chipTimer: number
  fracture: FractureReadout
  /** Beam start (the `mining.json beam.socket` muzzle) and end (contact point or max range), float64 world, while `beamOn`. */
  beamFrom: Vec3
  beamTo: Vec3
  /** Boom hulls only (M10 P5a12c): per-boom heat and beams; absent on the S/M single beam (its readout is unchanged). */
  booms?: BoomReadout
}

/** Per-boom state on a boom hull (P5a12c; `MAX_MINING_BOOMS` slots, `count` live). Boom 0 mirrors the fields above. */
export interface BoomReadout {
  /** Booms on the flown hull (0 = no boom hull this tick). */
  count: number
  /** Marked rocks (0..4). */
  marks: number
  /** 0..1 heat per boom. */
  readonly heat: Float64Array
  /** 1 while that boom is overheated. */
  readonly overheated: Uint8Array
  /** 1 while that boom fires; its beam runs from `from` to `to` (float64 world, 3 per boom). */
  readonly on: Uint8Array
  readonly from: Float64Array
  readonly to: Float64Array
  /** Mark slot each boom works (-1 = idle). */
  readonly mark: Int8Array
  /** Centre (float64 world, 3 per slot) and radius (m) of each marked rock, slots `0..marks-1` (the HUD mark rings). */
  readonly markPos: Float64Array
  readonly markR: Float64Array
  /** The hold is full: the booms idle. */
  holdFull: boolean
}

export function createBoomReadout(slots: number): BoomReadout {
  return {
    count: 0,
    marks: 0,
    heat: new Float64Array(slots),
    overheated: new Uint8Array(slots),
    on: new Uint8Array(slots),
    from: new Float64Array(slots * 3),
    to: new Float64Array(slots * 3),
    mark: new Int8Array(slots).fill(-1),
    markPos: new Float64Array(slots * 3),
    markR: new Float64Array(slots),
    holdFull: false,
  }
}

export function createMiningReadout(): MiningReadout {
  return {
    beamOn: false,
    contact: false,
    rock: createNavTarget(),
    mode: MINING_MODE.chip,
    power: 0,
    maxPower: 0,
    heat: 0,
    overheated: false,
    cooldownLeft: 0,
    chipTimer: 0,
    fracture: { active: false, progress: 0, instability: 0, windowCenter: 0.5, windowWidth: 0, tier: 0, tooHard: false },
    beamFrom: { x: 0, y: 0, z: 0 },
    beamTo: { x: 0, y: 0, z: 0 },
  }
}

/** `SalvageReadout.status`. */
/** `claimed` (M8 TR-8b1): the part is a raider-claimed jettison pod (`WreckPool.claimUntil` / `claimOwner`). */
export const SALVAGE_STATUS = { ok: 0, tooLarge: 1, holdFull: 2, outOfRange: 3, claimed: 4 } as const
export type SalvageStatus = (typeof SALVAGE_STATUS)[keyof typeof SALVAGE_STATUS]

export interface SalvageReadout {
  /** The player is firing the cutting beam (Salvage mode, trigger held). */
  beamOn: boolean
  /** Wreck slot under the beam, or -1. */
  wreck: number
  /** Part slot whose anchor the beam is on (or spilling into), or -1. */
  part: number
  /** Anchor index on `part` (0..MAX_ANCHORS_PER_PART-1), or -1. */
  anchor: number
  /** 0..1 cut progress of that anchor. */
  cutProgress: number
  /** The beam is spilling heat into `part` (off-anchor or on a finished anchor). */
  spilling: boolean
  /** The tractor (secondary trigger) is engaged. */
  tractorOn: boolean
  /** Part slot held by the tractor, or -1. */
  tractorPart: number
  /** `SALVAGE_STATUS` of the tractored (or last rejected) part. */
  status: SalvageStatus
  /** Cutting beam start and end, float64 world, while `beamOn`. */
  beamFrom: Vec3
  beamTo: Vec3
  /** Tractor beam end (the held part), float64 world, while `tractorOn`; the start is `beamFrom`. */
  tractorTo: Vec3
}

export function createSalvageReadout(): SalvageReadout {
  return {
    beamOn: false,
    wreck: -1,
    part: -1,
    anchor: -1,
    cutProgress: 0,
    spilling: false,
    tractorOn: false,
    tractorPart: -1,
    status: SALVAGE_STATUS.ok,
    beamFrom: { x: 0, y: 0, z: 0 },
    beamTo: { x: 0, y: 0, z: 0 },
    tractorTo: { x: 0, y: 0, z: 0 },
  }
}

/**
 * A rock scan (M2-09 `scanRock` writes it). Mk I: value and every ore share as a range (± `valueSpreadMk1`);
 * Mk II: one estimate within ±10% (a stable per-rock error), so low === high; Mk III: exact. Mk I-II label
 * tetherstone with its `unidentifiedName`.
 */
export interface RockScan {
  /** A result is present. */
  valid: boolean
  kind: NavKind
  index: number
  /** Scanner Mk used (1-3). */
  mk: number
  /** Remaining mass (tonnes). */
  massT: number
  mode: MiningMode
  /** 0..1 and its tier. */
  resistance: number
  tier: number
  valueLow: number
  valueHigh: number
  /** Share per ware index, low and high bounds (equal for Mk II and III). */
  shareLow: Float32Array
  shareHigh: Float32Array
  /** Mk III names tetherstone. */
  tetherstoneIdentified: boolean
  /** The rock's field grade multiplier (`galaxy.json minerals.grades.mult`); 0 outside every field (MC-a6). */
  grade: number
  /** The system's signature ore (ware index) when the rock's field holds it; -1 otherwise (MC-a6). */
  signature: number
}

export function createRockScan(): RockScan {
  return {
    valid: false,
    kind: NAV.none,
    index: -1,
    mk: 1,
    massT: 0,
    mode: MINING_MODE.chip,
    resistance: 0,
    tier: 0,
    valueLow: 0,
    valueHigh: 0,
    shareLow: new Float32Array(WARE_COUNT),
    shareHigh: new Float32Array(WARE_COUNT),
    tetherstoneIdentified: false,
    grade: 0,
    signature: -1,
  }
}

/** A wreck scan (M2-10 `scanWreck` writes it): one row per part, `partCount` rows used. Coolant is flagged at every Mk. */
export interface WreckScan {
  valid: boolean
  /** Wreck slot. */
  wreck: number
  /** The wreck's `WRECK_KIND` (M9 CH-3a: a `hulk` scans valid with no part rows). */
  kind: number
  mk: number
  partCount: number
  /** Per row (MAX_PARTS_PER_WRECK): part slot, component type index, condition, value range, tonnes, anchors. */
  part: Int32Array
  type: Uint8Array
  condition: Float32Array
  /** `CONDITION_BAND` of the row's condition (`salvage/generate.ts conditionBand`). */
  band: Uint8Array
  /** The part's Mk 1-3, 0 for a type without one (economy plan SY-4b: the scan row's "MK II · INSTALLABLE"). */
  partMk: Uint8Array
  valueLow: Float32Array
  valueHigh: Float32Array
  massT: Float32Array
  anchors: Uint8Array
  anchorsCut: Uint8Array
  /** 1 = the part still has an uncut coolant anchor. */
  coolant: Uint8Array
  freed: Uint8Array
  totalLow: number
  totalHigh: number
}

export function createWreckScan(): WreckScan {
  const n = MAX_PARTS_PER_WRECK
  return {
    valid: false,
    wreck: -1,
    kind: 0,
    mk: 1,
    partCount: 0,
    part: new Int32Array(n),
    type: new Uint8Array(n),
    condition: new Float32Array(n),
    band: new Uint8Array(n),
    partMk: new Uint8Array(n),
    valueLow: new Float32Array(n),
    valueHigh: new Float32Array(n),
    massT: new Float32Array(n),
    anchors: new Uint8Array(n),
    anchorsCut: new Uint8Array(n),
    coolant: new Uint8Array(n),
    freed: new Uint8Array(n),
    totalLow: 0,
    totalHigh: 0,
  }
}

/** The scan readout (owned by the tools runtime). */
export interface ScanReadout {
  /** What is being (or was last) scanned. */
  kind: NavKind
  index: number
  /** 0..1 of the hold time. */
  progress: number
  complete: boolean
  /** Filled for rock and fragment scans. */
  rock: RockScan
  /** Filled for wreck scans. */
  wreck: WreckScan
}

export function createScanReadout(): ScanReadout {
  return { kind: NAV.none, index: -1, progress: 0, complete: false, rock: createRockScan(), wreck: createWreckScan() }
}

export type DockingPhase = 'space' | 'cleared' | 'autodock' | 'docked'

/** Docking guides and state (M2-11 `corridorState` fills the geometric fields). Angles in degrees for display. */
export interface DockingReadout {
  phase: DockingPhase
  /** The cleared, auto-docking or docked station; -1 in open space without clearance. */
  station: number
  /** Seconds of clearance left (phase `cleared` or `autodock`). */
  clearanceLeft: number
  inCorridor: boolean
  /** Distance along the approach axis from the bay mouth (m; negative = inside the bay). */
  along: number
  /** Distance from the approach axis (m). */
  lateral: number
  /** Ship speed (m/s). */
  speed: number
  /** The limit that applies here (corridor or bay; 0 outside both). */
  speedLimit: number
  /** Nose error vs the bay's inward axis, and roll error vs the bay's up, degrees. */
  alignDeg: number
  rollDeg: number
  inBay: boolean
  /** Distance from the ship to the pad (m). */
  padDistance: number
  /** 0..1 of `dock.holdSeconds` inside the dock limits. */
  holdProgress: number
}

export function createDockingReadout(): DockingReadout {
  return {
    phase: 'space',
    station: -1,
    clearanceLeft: 0,
    inCorridor: false,
    along: 0,
    lateral: 0,
    speed: 0,
    speedLimit: 0,
    alignDeg: 0,
    rollDeg: 0,
    inBay: false,
    padDistance: 0,
    holdProgress: 0,
  }
}

// ---------------------------------------------------------------------------------------------
// Pools (SoA, fixed capacity; iterate `i < high` and test `alive[i]`)
// ---------------------------------------------------------------------------------------------

interface PoolHeader {
  readonly capacity: number
  /** Slots `[0, high)` may be alive. */
  high: number
  /** Live slots. */
  live: number
  readonly alive: Uint8Array
}

/** Loose ore chunks (M2-16). */
export interface ChunkPool extends PoolHeader {
  readonly x: Float64Array
  readonly y: Float64Array
  readonly z: Float64Array
  readonly vx: Float32Array
  readonly vy: Float32Array
  readonly vz: Float32Array
  /** Ware index. */
  readonly ware: Uint8Array
  readonly tonnes: Float32Array
  /** Game time spawned. */
  readonly born: Float64Array
  /** Per-chunk visual seed in [0, 1) (spin, scale jitter). */
  readonly seed: Float32Array
}

export function createChunkPool(capacity: number): ChunkPool {
  return {
    capacity,
    high: 0,
    live: 0,
    alive: new Uint8Array(capacity),
    x: new Float64Array(capacity),
    y: new Float64Array(capacity),
    z: new Float64Array(capacity),
    vx: new Float32Array(capacity),
    vy: new Float32Array(capacity),
    vz: new Float32Array(capacity),
    ware: new Uint8Array(capacity),
    tonnes: new Float32Array(capacity),
    born: new Float64Array(capacity),
    seed: new Float32Array(capacity),
  }
}

/** Rock fragments after a fracture (M2-16): static balls, rendered as smaller instances of the parent's archetype. */
export interface FragmentPool extends PoolHeader {
  /** Stable id of the parent rock (`FieldLayout.id`). */
  readonly parent: Int32Array
  /** Fragment number within the parent (fragments are regenerated from `(parent, k)`). */
  readonly k: Uint8Array
  readonly x: Float64Array
  readonly y: Float64Array
  readonly z: Float64Array
  readonly radius: Float32Array
  /** Archetype index 0-5 (the parent's). */
  readonly archetype: Uint8Array
  /** Shader seed in [0, 1). */
  readonly seed: Float32Array
  readonly massT: Float32Array
  readonly remainingT: Float32Array
  /** `MINING_MODE`. */
  readonly mode: Uint8Array
}

export function createFragmentPool(capacity: number): FragmentPool {
  return {
    capacity,
    high: 0,
    live: 0,
    alive: new Uint8Array(capacity),
    parent: new Int32Array(capacity),
    k: new Uint8Array(capacity),
    x: new Float64Array(capacity),
    y: new Float64Array(capacity),
    z: new Float64Array(capacity),
    radius: new Float32Array(capacity),
    archetype: new Uint8Array(capacity),
    seed: new Float32Array(capacity),
    massT: new Float32Array(capacity),
    remainingT: new Float32Array(capacity),
    mode: new Uint8Array(capacity),
  }
}

/**
 * `jettison` (M8 TR-8b1, M6 spec §6.4.5): a loose cargo pod the pilot dropped to end an interdiction, owned by the
 * raiders while its claim runs. It is never saved, so it has no `WRECK_KIND_NAMES` entry (a save can't name it).
 *
 * `hulk` (M9 CH-3a, D-TBD M9-4): a disabled, claimable ship (`SalvageRuntime.spawnHulk`): ship-shaped, with no parts
 * (H-10: only the claim) and no despawn timer. It is never saved either: the system's hulks are re-derived on entry
 * (seeded placement, the traffic manifest and `career.hulks`), so it has no `WRECK_KIND_NAMES` entry.
 */
export const WRECK_KIND = { combat: 0, death: 1, contract: 2, derelict: 3, jettison: 4, hulk: 5 } as const
export type WreckKindCode = (typeof WRECK_KIND)[keyof typeof WRECK_KIND]
/** The saved wreck kinds, indexed by code (`jettison` and `hulk` are left out: they are never saved). */
export const WRECK_KIND_NAMES = ['combat', 'death', 'contract', 'derelict'] as const
export type WreckKindName = (typeof WRECK_KIND_NAMES)[number]

/**
 * Wrecks (M2-17). A wreck's frame: ship wrecks use the ship's pose (origin at the ship origin); a derelict section
 * uses the derelict's rotation with its origin at the section centre. Part and anchor local positions are in
 * this frame.
 */
export interface WreckPool extends PoolHeader {
  /** `WRECK_KIND`. */
  readonly kind: Uint8Array
  /** Ship def index; -1 for a derelict section. */
  readonly defIndex: Int16Array
  /** Derelict section index (`salvage.json derelict.sections`); -1 otherwise. */
  readonly section: Int8Array
  readonly seed: Uint32Array
  readonly x: Float64Array
  readonly y: Float64Array
  readonly z: Float64Array
  readonly vx: Float32Array
  readonly vy: Float32Array
  readonly vz: Float32Array
  readonly qx: Float32Array
  readonly qy: Float32Array
  readonly qz: Float32Array
  readonly qw: Float32Array
  /** Bounding radius (m), for picking and brackets. */
  readonly radius: Float32Array
  /** Game time spawned. */
  readonly born: Float64Array
  /** Every part secured. */
  readonly looted: Uint8Array
  /** The death wreck's one-time loot check has run. */
  readonly lootChecked: Uint8Array
  readonly partCount: Uint8Array
  /** Stable key (`WreckRecord.key`), written at spawn. */
  readonly key: string[]
  /** Contract of a contract wreck, else null. */
  readonly contractId: (string | null)[]
  /**
   * M8 TR-8b1: a jettison pod can't be secured before this game time (`mirror.gameTime`, the clock of `born`); 0 for
   * every other wreck.
   */
  readonly claimUntil: Float64Array
  /** M8 TR-8b1: the Reach group that owns a jettison pod (its claim holds while the group lives), else `NO_CLAIM_OWNER`. */
  readonly claimOwner: Int32Array
}

/** `WreckPool.claimOwner` of an unowned wreck (the travel runtime's `NO_GROUP`, -1). */
export const NO_CLAIM_OWNER = -1

export function createWreckPool(capacity: number): WreckPool {
  return {
    capacity,
    high: 0,
    live: 0,
    alive: new Uint8Array(capacity),
    kind: new Uint8Array(capacity),
    defIndex: new Int16Array(capacity),
    section: new Int8Array(capacity),
    seed: new Uint32Array(capacity),
    x: new Float64Array(capacity),
    y: new Float64Array(capacity),
    z: new Float64Array(capacity),
    vx: new Float32Array(capacity),
    vy: new Float32Array(capacity),
    vz: new Float32Array(capacity),
    qx: new Float32Array(capacity),
    qy: new Float32Array(capacity),
    qz: new Float32Array(capacity),
    qw: new Float32Array(capacity),
    radius: new Float32Array(capacity),
    born: new Float64Array(capacity),
    looted: new Uint8Array(capacity),
    lootChecked: new Uint8Array(capacity),
    partCount: new Uint8Array(capacity),
    key: new Array<string>(capacity).fill(''),
    contractId: new Array<string | null>(capacity).fill(null),
    claimUntil: new Float64Array(capacity),
    claimOwner: new Int32Array(capacity).fill(NO_CLAIM_OWNER),
  }
}

/**
 * Salvageable parts (M2-17). Anchor columns hold `MAX_ANCHORS_PER_PART` entries per part: anchor `a` of part `p`
 * is at `p * MAX_ANCHORS_PER_PART + a`. World positions (`x/y/z`, `ax/ay/az`) are updated every tick, so the
 * HUD and fx project them directly.
 */
export interface PartPool extends PoolHeader {
  /** Wreck slot (while attached, and the wreck it came from once freed). */
  readonly wreck: Int32Array
  /** Part index within its wreck (key `${wreckKey}:${index}`). */
  readonly index: Uint8Array
  /** Component type index. */
  readonly type: Uint8Array
  /** Wreck-frame position of an attached part (m). */
  readonly lx: Float32Array
  readonly ly: Float32Array
  readonly lz: Float32Array
  /** World position, velocity and rotation. */
  readonly x: Float64Array
  readonly y: Float64Array
  readonly z: Float64Array
  readonly vx: Float32Array
  readonly vy: Float32Array
  readonly vz: Float32Array
  readonly qx: Float32Array
  readonly qy: Float32Array
  readonly qz: Float32Array
  readonly qw: Float32Array
  /** 0..1. */
  readonly condition: Float32Array
  /** Mk 1-3 of a Mk-carrying component, 0 for the rest (economy plan SY-4a; `salvage/generate.ts rollMk`). */
  readonly mk: Uint8Array
  /** Who made the part (M8 MC-f5a): its `MAKER_IDS` index, -1 = unknown (`salvage/generate.ts makerCode`). */
  readonly maker: Int8Array
  /** 1 = a Rush-pattern part (M8 MC-h1 sets it; the scan row and the career's `provenance: 'rush'` read it). */
  readonly rush: Uint8Array
  readonly massT: Float32Array
  readonly anchorCount: Uint8Array
  /** Anchor wreck-frame positions (m). */
  readonly alx: Float32Array
  readonly aly: Float32Array
  readonly alz: Float32Array
  /** Anchor world positions. */
  readonly ax: Float64Array
  readonly ay: Float64Array
  readonly az: Float64Array
  /** Anchor cut progress 0..1 (1 = cut). */
  readonly cut: Float32Array
  /** 1 = coolant anchor. */
  readonly coolant: Uint8Array
  /** Seconds to cut each anchor at cut multiplier 1. */
  readonly cutSeconds: Float32Array
  /** Every anchor cut: the part is a free physics body. */
  readonly freed: Uint8Array
  readonly secured: Uint8Array
  /** Held by a tractor this tick. */
  readonly tractored: Uint8Array
  /** A cargo pod's contents (death pods), else null. */
  readonly contents: (CargoContents | null)[]
}

export function createPartPool(capacity: number): PartPool {
  const a = capacity * MAX_ANCHORS_PER_PART
  return {
    capacity,
    high: 0,
    live: 0,
    alive: new Uint8Array(capacity),
    wreck: new Int32Array(capacity),
    index: new Uint8Array(capacity),
    type: new Uint8Array(capacity),
    lx: new Float32Array(capacity),
    ly: new Float32Array(capacity),
    lz: new Float32Array(capacity),
    x: new Float64Array(capacity),
    y: new Float64Array(capacity),
    z: new Float64Array(capacity),
    vx: new Float32Array(capacity),
    vy: new Float32Array(capacity),
    vz: new Float32Array(capacity),
    qx: new Float32Array(capacity),
    qy: new Float32Array(capacity),
    qz: new Float32Array(capacity),
    qw: new Float32Array(capacity),
    condition: new Float32Array(capacity),
    mk: new Uint8Array(capacity),
    maker: new Int8Array(capacity).fill(-1),
    rush: new Uint8Array(capacity),
    massT: new Float32Array(capacity),
    anchorCount: new Uint8Array(capacity),
    alx: new Float32Array(a),
    aly: new Float32Array(a),
    alz: new Float32Array(a),
    ax: new Float64Array(a),
    ay: new Float64Array(a),
    az: new Float64Array(a),
    cut: new Float32Array(a),
    coolant: new Uint8Array(a),
    cutSeconds: new Float32Array(a),
    freed: new Uint8Array(capacity),
    secured: new Uint8Array(capacity),
    tractored: new Uint8Array(capacity),
    contents: new Array<CargoContents | null>(capacity).fill(null),
  }
}

export const SECURED_LOG_SIZE = 32

/** A secured part's details (`SEV.partSecured.c` indexes the log). */
export interface SecuredPart {
  /** `${wreckKey}:${partIndex}`: the `CargoComponent.id` (data cores are logged by it). */
  key: string
  type: ComponentType
  condition: number
  /** Mk 1-3, or 0 for a type without one (economy plan SY-4a). */
  mk: number
  /** Who made the part (M8 MC-f5a), or null when unknown; the career copies it to `CargoComponent.maker`. */
  maker: MakerId | null
  /** A Rush-pattern part (M8 MC-h1); the career copies it as `CargoComponent.provenance: 'rush'`. */
  rush: boolean
  massT: number
  value: number
  contractId: string | null
  contents: CargoContents | null
}

/** A preallocated ring of the last `SECURED_LOG_SIZE` secured parts; the next write goes to `head % SECURED_LOG_SIZE`. */
export interface SecuredLog {
  readonly entries: SecuredPart[]
  head: number
}

export function createSecuredLog(): SecuredLog {
  const entries: SecuredPart[] = []
  for (let i = 0; i < SECURED_LOG_SIZE; i++) {
    entries.push({ key: '', type: 'reactor', condition: 0, mk: 0, maker: null, rush: false, massT: 0, value: 0, contractId: null, contents: null })
  }
  return { entries, head: 0 }
}

/** Live encounter groups (M2-15). */
export interface EncounterGroups {
  readonly capacity: number
  readonly active: Uint8Array
  /** Group id (≥ 1; `EncounterTag.group` of its ships). */
  readonly id: Int32Array
  /** `ENCOUNTER_KIND`. */
  readonly kind: Uint8Array
  /** Ships of the group spawned so far, and still alive. */
  readonly total: Uint8Array
  readonly alive: Uint8Array
  /** Contract groups spawn lazily near their marker; raider and repo groups spawn at once. */
  readonly spawned: Uint8Array
  /** Spawn centre (or the contract marker while pending), float64 world. */
  readonly x: Float64Array
  readonly y: Float64Array
  readonly z: Float64Array
  readonly contractId: (string | null)[]
}

export function createEncounterGroups(capacity: number): EncounterGroups {
  return {
    capacity,
    active: new Uint8Array(capacity),
    id: new Int32Array(capacity),
    kind: new Uint8Array(capacity),
    total: new Uint8Array(capacity),
    alive: new Uint8Array(capacity),
    spawned: new Uint8Array(capacity),
    x: new Float64Array(capacity),
    y: new Float64Array(capacity),
    z: new Float64Array(capacity),
    contractId: new Array<string | null>(capacity).fill(null),
  }
}

// ---------------------------------------------------------------------------------------------
// World deltas (the save's per-system world record, M2-12)
// ---------------------------------------------------------------------------------------------

/** Depleted and fractured rocks. Untouched rocks are regenerated from the field seed. */
export interface RockDeltas {
  /** [stable rock id, remaining tonnes] for partly mined rocks. */
  remaining: [number, number][]
  /** Stable ids of rocks gone (depleted, fractured or burst). */
  removed: number[]
  /** Fractured parents and their fragments' remaining tonnes (fragments regenerate from `(parent, k)`). */
  fractured: { parent: number; fragments: [k: number, tonnes: number][] }[]
}

export interface WreckPartRecord {
  type: ComponentType
  condition: number
  /**
   * Mk as rolled (economy plan SY-4a), so a reloaded wreck keeps it even where the region table differs. Optional: an
   * older record re-derives the same Mk from its seed.
   */
  mk?: number
  /**
   * Who made the part (M8 MC-f5a), written only where the seed can't re-derive it (a ship wreck whose caller named a
   * maker, MC-f5b); a derelict's parts re-derive `first_charter`. Absent in older saves.
   */
  maker?: MakerId
  /**
   * The Rush-pattern tag as rolled (M8 MC-h1), so a reloaded wreck keeps it; written only when set. Absent: the part
   * keeps what its seed re-derives.
   */
  rush?: boolean
  /** Cut progress per anchor (0..1). */
  cut: number[]
  freed: boolean
  secured: boolean
  /** Pose of a freed part. */
  pos?: Vec3Tuple
  vel?: Vec3Tuple
  rot?: QuatTuple
  contents?: CargoContents
}

/** One live wreck: parts regenerate from the seed and layout, the record keeps their state. */
export interface WreckRecord {
  /**
   * Stable key (combat `c<seq>`, death `d<seq>`, contract `k:<contractId>`, derelict `h<seed36>:<sectionId>`). Old saves
   * with a derelict `h:<sectionId>` are re-keyed from `seed` and `section` on restore (BUG-B1).
   */
  key: string
  kind: WreckKindName
  /** Ship id (combat, death and contract wrecks). */
  defId?: string
  /** Derelict section id. */
  section?: string
  seed: number
  pos: Vec3Tuple
  vel: Vec3Tuple
  rot: QuatTuple
  /** Game time spawned. */
  bornAt: number
  lootChecked: boolean
  contractId?: string
  parts: WreckPartRecord[]
}

/** One extra cuttable derelict (M8 MC-h2): its pose, the seed of its parts and what it is to the Rush roll. */
export interface DerelictSpot {
  readonly pose: { readonly position: Readonly<Vec3>; readonly rotation: Readonly<Quat> }
  readonly seed: number
  readonly site: DerelictSite
}

export interface SalvageDeltas {
  /** Next wreck key sequence number (keys never repeat within a career, so data cores log once). */
  nextSeq: number
  wrecks: WreckRecord[]
}

/** A clear-raiders group not yet defeated (spawned lazily near its marker; live ships are not saved). */
export interface EncounterGroupRecord {
  contractId: string
  marker: Vec3Tuple
  /** Contract tier (index into `encounters.json contractTiers`). */
  tier: number
}

export interface EncounterDeltas {
  /** Game time of the next director roll. */
  nextEncounterAt: number
  /** Game time the repo crew is due, or -1. */
  repoDueAt: number
  /** Last group id handed out. */
  groupSeq: number
  groups: EncounterGroupRecord[]
}

/** Lazy saturation state of one item at one station: `s` as of game time `t` (M2-07 decays it on read). */
export interface MarketEntry {
  s: number
  t: number
}

/** Station id → item key (a ware id, or `component:<type>`) → saturation. Missing = unsaturated. */
export type MarketState = Record<StationId, Record<string, MarketEntry>>

/** Market item key of a ware or component type. */
export function marketKey(item: WareId | { component: ComponentType }): string {
  return typeof item === 'string' ? item : `component:${item.component}`
}

/** The per-system world record of a save (`SaveRecord.worlds[systemId]`). */
export interface WorldDeltas {
  rocks: RockDeltas
  salvage: SalvageDeltas
  markets: MarketState
  encounters: EncounterDeltas
}

export function emptyRockDeltas(): RockDeltas {
  return { remaining: [], removed: [], fractured: [] }
}

export function emptySalvageDeltas(): SalvageDeltas {
  return { nextSeq: 0, wrecks: [] }
}

export function emptyEncounterDeltas(): EncounterDeltas {
  return { nextEncounterAt: 0, repoDueAt: -1, groupSeq: 0, groups: [] }
}

export function emptyWorldDeltas(): WorldDeltas {
  return { rocks: emptyRockDeltas(), salvage: emptySalvageDeltas(), markets: {}, encounters: emptyEncounterDeltas() }
}

// ---------------------------------------------------------------------------------------------
// Runtimes (one per system; stubs in src/space/{stations,docking,tools,rocks,salvage,encounters}.ts)
// ---------------------------------------------------------------------------------------------

/** The docking bay in world space (M2-11 `bayFrameFromSocket`). */
export interface BayFrame {
  /** The bay-box centre (the `DOCK_BAY_0` socket), float64 world. */
  center: Vec3
  /** Where a docked ship's origin rests, float64 world. */
  pad: Vec3
  /** Centre of the open mouth face (center + out × depth / 2), float64 world. */
  mouth: Vec3
  /** Unit world axes of the socket: out = local −Z (through the mouth), up = +Y, right = +X. */
  out: Vec3
  up: Vec3
  right: Vec3
  /** Bay box extents (socket params `bayW`, `bayH`, `bayD`), metres. */
  width: number
  height: number
  depth: number
}

export interface StationRuntime {
  def: StationDef
  index: number
  /** Station origin, float64 world. */
  pos: Vec3
  rot: Quat
  /** Bounding radius (m); 0 until the model is attached. */
  radius: number
  /** Null until the model is attached. */
  bay: BayFrame | null
  /** Model attached: bay frame and static collider exist; docking and launch are allowed. */
  ready: boolean
}

export interface StationsRuntime {
  /** One per `stations.json` station, in data order (`setStations` refills this same array in place). */
  readonly list: StationRuntime[]
  /** Additive (M3 R22e): bumps whenever `setStations` replaces the list, so views kept per index rebuild. */
  readonly version?: number
  /**
   * Additive (M3 R22e): replaces the stations with `defs` (the current Reach system's, from `bridgeStations`). Every
   * old collider is removed, the list is refilled in place, poses come from the manifest and models already in the
   * live map are attached again. `colliders: false` stops the runtime building station colliders (the Reach streams
   * them near the player instead) and stays in force for later `attachModel` calls. A rare event; allocates.
   */
  setStations?(defs: readonly StationDef[], opts?: { readonly colliders?: boolean }): void
  /** Builds the bay frame and the static collider from the loaded station model; marks the station ready. */
  attachModel(index: number, model: LoadedModel): void
  /** Station whose hull is nearest a world point (centre distance less its radius), or -1. */
  nearest(x: number, y: number, z: number): number
  /** The station nearest the ray (unit direction) within `coneRad` and `maxRange`, else -1. Allocation-free. */
  pick(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, coneRad: number, maxRange: number): number
}

/**
 * M10 P5a9b2: an own L the player's S may dock into (`DockingRuntime.setCradleCarrier`): its live entity, its hangar id
 * (`Career.carriedBy`), its `DOCK_CRADLE` socket (`docking/cradle.ts cradleSocketOf`) and its bounds radius (m).
 * `onCarried` hears the hangar id when the S is cradled and `undefined` when it leaves (the Career write).
 */
export interface CradleCarrierLink {
  readonly eid: number
  readonly fleetId: string
  readonly socket: BaySocket
  readonly radius: number
  readonly onCarried?: (fleetId: string | undefined) => void
}

/** M10 P5a9b2: the cradle flow as plain JSON (`DockingRuntime.cradleStatus`). */
export interface CradleStatus {
  phase: 'none' | 'approach' | 'cradled'
  holdOrdered: boolean
  carried: boolean
  /** The registered carrier's entity, or -1. */
  carrier: number
}

export interface DockingRuntime {
  readonly readout: DockingReadout
  /** Requests clearance (emits `clearanceGranted` or `clearanceDenied`). True when granted. */
  request(station: number): boolean
  /**
   * The range from station `station`'s hull within which `request` can grant the player's hull: `moor.rangeM` when it
   * moors there (the bay doesn't take it), else `docking.requestRange`. The dock key picks stations by it.
   */
  requestRange(station: number): number
  /** Starts the autopilot (needs clearance and the hull within `autodockRange`; forces FA on). True when started. */
  startAutodock(): boolean
  cancelAutodock(): void
  /** Places the player with its hull box centred on the station's pad, facing inward, at rest; phase `docked`. */
  dockAt(station: number): void
  /** Releases the player with its hull box centred on the mouth, facing out at `launch.speed`; phase `space`; emits `undocked`. */
  launch(station: number): void
  /**
   * M10 P5a9b2 (optional, so the m8e stubs stay valid): the dock key's cradle path. With an own L registered
   * (`setCradleCarrier`) it requests the L's cradle when the flown S is in range and the L holds, swallows the key
   * while the cradle approach flies, and launches while carried. True when it handled the key (no station pick).
   */
  cradleKey?(): boolean
  /** M10 P5a9b2: registers the own L whose cradle the dock key may ask (null: none, today's game). A rare call. */
  setCradleCarrier?(link: CradleCarrierLink | null): void
  /** M10 P5a9b2: launches the carried S out of the cradle mouth at `cradle.launchSpeedMps`. False when not carried. */
  launchCradle?(): boolean
  /** M10 P5a9b2: the cradle flow for tests and the HUD: phase (`none`, `approach`, `cradled`), the hold order, carried. */
  cradleStatus?(): CradleStatus
  /** Runs the autopilot (cancelled by `input.flightActive`); records the pre-step velocity for impacts. */
  control: SpaceSubsystem
  /** Readout, dock hold (→ `docked`) and station impacts. */
  physics: SpaceSubsystem
  /** Clearance expiry. */
  end: SpaceSubsystem
}

export interface ToolsRuntime {
  readonly player: PlayerToolState
  readonly scan: ScanReadout
  setMode(mode: ToolMode): void
  /** Consumes the input edges, writes the player's `ToolControl`, picks targets, scans, requests docking. */
  control: SpaceSubsystem
  /** Player death → `playerDied`, cancels auto-dock, clears clearance. */
  deaths: SpaceSubsystem
}

/**
 * The galaxy field mix at a float64 system-frame position (MC-a5a): writes the field's ore shares in M2 `WARE_IDS`
 * order into `out` (length ≥ `WARE_COUNT`; all zeros = no field, archetype only) and returns the field's grade
 * multiplier (1 outside every field). Allocation-free.
 */
export type FieldMixAt = ((x: number, y: number, z: number, out: Float32Array) => number) & {
  /** The system's signature ore as a ware index (-1 or absent: none); the scanner's signature line (MC-a6). */
  readonly signature?: number
  /**
   * The signature ore the field at a position holds (PF-4): an ice field in a Ferrite · Ice system holds ice, not
   * ferrite; -1 for none. Absent: `signature` for any field. Allocation-free.
   */
  readonly signatureAt?: (x: number, y: number, z: number) => number
  /**
   * MC-e6b: the ore the field at a position keeps through the `mix.maxOres` cut (`rockComposition` `keepWare`): the
   * refractory ore in a refractory-signature system's field that holds it; -1 for none. Absent: none. Allocation-free.
   */
  readonly keepAt?: (x: number, y: number, z: number) => number
}

/** A rock's field facts (MC-a6b, the rock tint): `RockScan.grade`/`signature` without a scan. Mutable scratch. */
export interface RockFieldFacts {
  /** The field grade multiplier, 0 outside every field (or the blend off). */
  grade: number
  /** The system's signature ware when the rock's field holds it, else -1. */
  signature: number
}

export interface RocksRuntime {
  readonly readout: MiningReadout
  readonly chunks: ChunkPool
  readonly fragments: FragmentPool
  /**
   * The rock or fragment nearest the ray (unit direction) within `coneRad`, else the nearest within `maxRange`:
   * writes `out` and returns true, or returns false and leaves `out` unchanged. Allocation-free.
   */
  pick(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, coneRad: number, maxRange: number, out: NavTarget): boolean
  /** Scans a rock or fragment with scanner Mk `scannerMk` into `out` (range is the caller's check). False when it is gone. */
  scan(kind: NavKind, index: number, scannerMk: number, out: RockScan): boolean
  /** World centre of a rock or fragment into `out`. False when it is gone. */
  position(kind: NavKind, index: number, out: Vec3): boolean
  /** Radius (m) of a rock or fragment; 0 when it is gone. */
  radius(kind: NavKind, index: number): number
  exportDeltas(): RockDeltas
  importDeltas(deltas: RockDeltas): void
  /**
   * M3 bridge (anomaly hotspots): the fracture tetherstone chance at a rock's float64 position (negative keeps
   * `mining.json tetherstone.chance`), or null for the default everywhere. Rocks touched after the call use it.
   */
  setTetherstoneChance(chanceAt: ((x: number, y: number, z: number) => number) | null): void
  /**
   * M8 bridge (MC-a5a): the galaxy field mix at a rock's float64 position, or null for archetype-only rocks. The
   * runtime calls it only when a rock's composition is built, never per frame (MC-a5b reads it; nothing does yet).
   * Optional so test fakes of this interface need not stub it; `createRocksRuntime` always provides it.
   */
  setFieldMixAt?(mixAt: FieldMixAt | null): void
  /**
   * MC-a6b (the rock tint): rock `index`'s field grade and signature into `out` from the per-rock caches (an
   * untouched rock's are filled from the field mix lookup, as its composition will be). False (0, -1) when the index
   * is outside the sim's current layout. Not per frame (it may ask the lookup); allocation-free. Optional like
   * `setFieldMixAt`.
   */
  fieldFacts?(index: number, out: RockFieldFacts): boolean
  /** MC-a6b: bumped by every `setFieldMixAt` call, so a view knows when to refresh its rock tint. */
  readonly fieldMixVersion?: number
  /**
   * Boom hulls (M10 P5a12c): the booms on the player's flown def (0 = none), the marks, and the target key's
   * mark toggle (a `MARK_RESULT`, or 0 when the rock is outside `booms.rangeM` of the ship or gone). Optional so test
   * fakes need not stub them; `createRocksRuntime` always provides them.
   */
  boomCount?(): number
  readonly marks?: BoomMarks
  toggleMark?(kind: NavKind, index: number): number
  /** Mining beam, chip, fracture, heat, bursts. */
  tools: SpaceSubsystem
  /** Chunk drift, the collector cone and capture. */
  physics: SpaceSubsystem
}

export interface SalvageRuntime {
  readonly readout: SalvageReadout
  readonly wrecks: WreckPool
  readonly parts: PartPool
  readonly securedLog: SecuredLog
  /**
   * The wreck nearest the ray (unit direction) within `coneRad`, else the nearest within `maxRange`: writes `out`
   * (`NAV.wreck`) and returns true, or returns false and leaves `out` unchanged. Allocation-free.
   */
  pick(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, coneRad: number, maxRange: number, out: NavTarget): boolean
  /** Scans a wreck into `out`. False when it is gone. */
  scan(wreck: number, scannerMk: number, out: WreckScan): boolean
  /**
   * A combat wreck of ship def `defIndex` at a pose; returns the wreck slot or -1 (pool full). `maker` (M8 MC-f5b)
   * names who built the ship, so its parts carry that maker; absent or null = none (raider, guardian, player and corp
   * ships). No RNG draw depends on it.
   */
  spawnCombatWreck(defIndex: number, pos: Readonly<Vec3>, vel: Readonly<Vec3>, rot: Readonly<Quat>, seed: number, maker?: MakerId | null): number
  /**
   * M8 MC-f5b: binds (null unbinds) the hook a local kill's wreck reads its maker through: `makerOf(eid)` is
   * called once per killed ship with a layout, on the sim's `deaths` stage, and returns the maker or null. Bound by the
   * traffic layer from its contact store (space never imports traffic). Unbound → kill wrecks carry no maker.
   */
  bindMakerOf(makerOf: ((eid: number) => MakerId | null) | null): void
  /** The player's death wreck: the player's ship with one cargo pod holding `contents`. */
  spawnDeathWreck(pos: Readonly<Vec3>, vel: Readonly<Vec3>, rot: Readonly<Quat>, contents: CargoContents): number
  /** A recovery-contract wreck at `marker` carrying one part of `componentType` tagged with the contract. */
  spawnContractWreck(contractId: string, marker: Readonly<Vec3Tuple>, componentType: ComponentType): number
  removeContractWreck(contractId: string): void
  /**
   * M7 S37d: a story wreck (the Mission 2 barge) of ship def `defId` at a fixed pose, keyed `key` (its parts are
   * `<key>:<index>`). Kind `contract` with no contract id: it never despawns until looted. Its parts are the def's
   * layout rolled from `hashString(key)` (deterministic). Idempotent: an alive wreck with that key is returned as it is.
   * Returns the wreck slot, or -1 (unknown def, no layout, or a full pool). A rare event.
   */
  spawnStoryWreck(key: string, defId: string, pos: Readonly<Vec3Tuple>, rot: Readonly<QuatTuple>): number
  /**
   * M8 TR-8b1 (M6 spec §6.4.2, §6.4.5): a loose cargo pod holding exactly `contents` at `pos`, at rest, already freed
   * (the cargo-pod prop's collider and radius, as a freed part). Kind `jettison`, key `j<seq>` (the part is
   * `j<seq>:0`). The tractor refuses it with `SALVAGE_STATUS.claimed` (`partRejected` `REJECT_REASON.claimed`, no
   * `partSecured`) while `mirror.gameTime < claimUntil`, or while `owner` is a live group (`bindClaimOwnerAlive`).
   * It despawns after `wrecks.combatLifeSeconds` and is never saved (`exportDeltas` skips it). Returns the wreck slot,
   * or -1 (the pool is full of non-combat wrecks). A rare event.
   */
  spawnJettisonPod(pos: Readonly<Vec3>, contents: CargoContents, claimUntil: number, owner: number): number
  /** M8 TR-8b1: despawns jettison pod `wreck` (`wreckDespawned`); a no-op for any other or dead slot. A rare event. */
  despawnJettisonPod(wreck: number): void
  /**
   * M8 TR-8b1: binds (null unbinds) the hook a jettison pod's claim asks whether its owner group is alive (the Reach
   * session's `groupAlive(group) > 0`; space never imports the Reach). Called only when the tractor brings an owned
   * pod to the hold. Unbound → only `claimUntil` holds the claim.
   */
  bindClaimOwnerAlive(alive: ((group: number) => boolean) | null): void
  /**
   * M9 CH-3a: a hulk (`WRECK_KIND.hulk`) of ship def `defIndex` at rest at `pos`/`rot`, keyed `key` (a seeded hulk's
   * `hk:<sys>:<n>` or an off-screen wreck's `ow-<seq>`): the ship's hull body and no parts (H-10), never despawned by
   * time and never saved (`exportDeltas` skips it). Idempotent: an alive hulk with that key is returned as it is.
   * Returns the wreck slot, or -1 (a def without a model, no room, or a pool full of non-combat wrecks).
   * Allocation-free apart from the slot's body (a rare event).
   *
   * PG-2 (R2-4): `claimed` true = the hulk holds an open claim (an `ow-` record's `claim` 2, an open `career.hulks`
   * site). Room: at most `salvage.json hulks.maxLocal` unclaimed and `hulks.maxClaimed` claimed hulks live, and
   * `maxLocal + maxClaimed` in all, so a system where the player holds claims still shows its other hulks. A re-assert
   * of a live key updates its claim state (`markHulkClaimed`).
   */
  spawnHulk(defIndex: number, pos: Readonly<Vec3>, rot: Readonly<Quat>, key: string, claimed?: boolean): number
  /**
   * PG-2 (R2-4): records that the live hulk `key` now holds (or no longer holds) an open claim, moving it between the
   * `maxLocal` and `maxClaimed` counts. False when no hulk `key` is live, or when marking it claimed would pass
   * `maxClaimed` (it stays counted unclaimed). A rare event.
   */
  markHulkClaimed(key: string, claimed: boolean): boolean
  /** M9 CH-3a: despawns the hulk keyed `key` (`wreckDespawned`); a no-op when none is alive. A rare event. */
  despawnHulk(key: string): void
  exportDeltas(): SalvageDeltas
  importDeltas(deltas: SalvageDeltas): void
  /**
   * Moves the derelict's sections to `pose` (a `DerelictPose`) with parts from `seed`; null removes them (M3 bridge:
   * each system's derelict POI). Sections already there with that seed keep their progress. `site` (M8 MC-h1) is what
   * the site is to the Rush-pattern roll (`plain` when omitted; `sealed` for the Harrow and story sites). A rare event.
   *
   * `extras` (M8 MC-h2): the other cuttable derelicts of the system (a derelict-graveyard system's extra salvage
   * sites, `bridge/m2/systemWorld.ts graveyardSalvageSites`), each with its own pose, seed and site. Their sections
   * live and save like the main derelict's (keyed by their seed); none when omitted. They never move the main one.
   */
  setDerelict(
    pose: { readonly position: Readonly<Vec3>; readonly rotation: Readonly<Quat> } | null,
    seed: number,
    site?: DerelictSite,
    extras?: readonly DerelictSpot[],
  ): void
  /**
   * The galaxy region of the system the salvage world is in (economy plan SY-4b; the system's `region`, never a random
   * draw). Derelict parts roll their Mk for it from the next roll on (`setDerelict` and a restored derelict record),
   * so the bridge sets it before `setDerelict`. `gate` until set (`DEFAULT_SALVAGE_REGION`). A rare event.
   */
  setRegion(region: RegionId): void
  /** Cutting beam, anchors, coolant vents, tractor forces. */
  tools: SpaceSubsystem
  /** Body sync and tractor capture (`partSecured` / `partRejected`). */
  physics: SpaceSubsystem
  /** Combat wrecks from this tick's M1 `kill` events. */
  deaths: SpaceSubsystem
  /** Despawn timers, the death wreck's loot check, `wreckLooted`. */
  end: SpaceSubsystem
}

export interface EncountersRuntime {
  readonly groups: EncounterGroups
  /** Registers a clear-raiders group (spawned when the player comes within `spawnWithin` of the marker). */
  spawnContractGroup(contractId: string, marker: Readonly<Vec3Tuple>, tier: number): void
  cancelContractGroup(contractId: string): void
  /** The contract of group `group` (valid for any group id still in the event ring), else null. */
  contractOf(group: number): string | null
  /** Feeds the repo timing (called on launch). */
  notifyUndock(): void
  exportDeltas(): EncounterDeltas
  importDeltas(deltas: EncounterDeltas): void
  /** Group losses from this tick's `kill` events → `groupDefeated`. */
  deaths: SpaceSubsystem
  /** The director, repo crew, lazy contract groups and quiet despawns. */
  end: SpaceSubsystem
}

// ---------------------------------------------------------------------------------------------
// The space world and the `m2` service
// ---------------------------------------------------------------------------------------------

/** Everything a space runtime may read; built once by `createSpaceWorld` (M2-04) before the runtimes. */
export interface SpaceContext {
  readonly sim: Sim
  readonly game: GameData
  readonly data: M2Data
  /**
   * The M2 RNG, seeded from the career seed (never `sim.rng`, plan §2.11). Re-seeded in place
   * (`rng.state = careerSeed >>> 0`) when a career starts or loads.
   */
  readonly rng: Rng
  readonly events: SpaceEventRing
  readonly input: PlayerToolInput
  readonly player: PlayerToolState
  readonly mirror: CareerMirror
  /**
   * Every loaded model by asset id (the M1 ship models from boot, the deferred station and prop models as they
   * load). A live map: entries appear while the game runs.
   */
  readonly models: ReadonlyMap<string, LoadedModel>
}

export function createSpaceContext(
  sim: Sim,
  game: GameData,
  data: M2Data,
  models: ReadonlyMap<string, LoadedModel>,
  careerSeed: number,
): SpaceContext {
  return {
    sim,
    game,
    data,
    rng: createRng(careerSeed),
    events: createSpaceEventRing(),
    input: createPlayerToolInput(),
    player: createPlayerToolState(),
    mirror: createCareerMirror(data),
    models,
  }
}

/** Dependencies of the tools runtime. */
export interface ToolsDeps {
  stations: StationsRuntime
  docking: DockingRuntime
  rocks: RocksRuntime
  salvage: SalvageRuntime
}

export interface SpaceWorld extends SpaceContext {
  readonly stations: StationsRuntime
  readonly docking: DockingRuntime
  readonly tools: ToolsRuntime
  readonly rocks: RocksRuntime
  readonly salvage: SalvageRuntime
  readonly encounters: EncountersRuntime
}

/** The `m2` service (career mode only), provided by the career feature (M2-22). */
export interface M2Runtime {
  readonly data: M2Data
  readonly space: SpaceWorld
  /** The current Career (immutable snapshot; changes only through commands). */
  career(): Career
  readonly ui: UiStoreApi
  /** Same live map as `space.models`. */
  readonly models: ReadonlyMap<string, LoadedModel>
  /** Every deferred station and prop model has loaded (Launch waits for it). */
  assetsReady: boolean
}
